import React from 'react';
import { TextField, MenuItem, IconButton, Typography, Box, Select, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import InfoIcon from '@mui/icons-material/Info';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
} from '@hello-pangea/dnd';
import { FormLabelStyled } from '../../../common/Dialog/DialogGlynt';
import AddNewButton from './AddNewButton';
import { NamingElement, NamingSeparator } from '../../../../types/packager-types';
import { METADATA_PATHS } from '../templateEditorConstants';
import { validateDateFormat } from '../templateEditorUtils';

const DATE_FORMAT_HELP = [
  '%Y - 4-digit year (2025)',
  '%m - Month (01-12)',
  '%d - Day (01-31)',
  '%H - Hour 24h (00-23)',
  '%M - Minute (00-59)',
  '%S - Second (00-59)',
  '%y - 2-digit year',
  '%b - Month abbr (Jan)',
  '%B - Month name (January)',
];

interface TemplateNamingSectionProps {
  namingElements: NamingElement[];
  namingSeparator: NamingSeparator;
  namingMaxLength: number;
  splitBy?: string;
  error?: string;
  onAdd: () => void;
  onUpdate: (index: number, updates: Partial<NamingElement>) => void;
  onRemove: (index: number) => void;
  onMove: (fromIndex: number, toIndex: number) => void;
  onSeparatorChange: (separator: NamingSeparator) => void;
  onMaxLengthChange: (length: number) => void;
  getNamingPreview: () => string;
  documentDataOptions: Array<{ value: string; label: string }>;
}

const NamingElementRow: React.FC<{
  element: NamingElement;
  index: number;
  splitBy?: string;
  documentDataOptions: Array<{ value: string; label: string }>;
  onUpdate: (index: number, updates: Partial<NamingElement>) => void;
  onRemove: (index: number) => void;
  provided: DraggableProvided;
  snapshot: DraggableStateSnapshot;
}> = ({ element, index, splitBy, documentDataOptions, onUpdate, onRemove, provided, snapshot }) => {
  return (
    <Box
      ref={provided.innerRef}
      {...provided.draggableProps}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        mb: 1,
        p: 1,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        backgroundColor: 'background.paper',
        boxShadow: snapshot.isDragging ? 4 : 0,
        zIndex: snapshot.isDragging ? 1300 : 'auto',
      }}
    >
      <span
        {...provided.dragHandleProps}
        style={{
          cursor: 'grab',
          display: 'flex',
          alignItems: 'center',
          marginRight: 8,
          marginTop: 4,
        }}
      >
        <DragIndicatorIcon color="disabled" />
      </span>
      <Select
        size="small"
        variant="outlined"
        value={element.type}
        onChange={(e) =>
          onUpdate(index, { type: e.target.value as NamingElement['type'], value: '' })
        }
        sx={{
          width: 140,
          fontSize: 13,
          height: 32,
          minHeight: 32,
          backgroundColor: 'background.paper',
        }}
      >
        <MenuItem value="STRING_LITERAL">String Literal</MenuItem>
        <MenuItem value="METADATA">Metadata</MenuItem>
        <MenuItem value="DOCUMENT_DATA">Document Data</MenuItem>
        <MenuItem value="SPLIT_VALUE">Split Value</MenuItem>
      </Select>
      {element.type === 'STRING_LITERAL' && (
        <TextField
          size="small"
          variant="outlined"
          placeholder="e.g., prefix"
          value={element.value || ''}
          onChange={(e) => onUpdate(index, { value: e.target.value })}
          sx={{
            flexGrow: 1,
            minWidth: 150,
            fontSize: 13,
            backgroundColor: 'background.paper',
            height: 32,
          }}
          InputProps={{
            sx: { height: 32, minHeight: 32, fontSize: 13 },
          }}
        />
      )}
      {element.type === 'METADATA' && (
        <>
          <Select
            size="small"
            variant="outlined"
            value={element.value || ''}
            onChange={(e) => onUpdate(index, { value: e.target.value, format: undefined })}
            sx={{
              flexGrow: 1,
              minWidth: 150,
              fontSize: 13,
              height: 32,
              minHeight: 32,
              backgroundColor: 'background.paper',
            }}
          >
            <MenuItem value="">Select metadata path...</MenuItem>
            {METADATA_PATHS.map((p) => (
              <MenuItem key={p.value} value={p.value}>
                {p.label}
              </MenuItem>
            ))}
          </Select>
          {(element.value === 'document.created_at' || element.value === 'document.updated_at') && (
            <>
              <TextField
                size="small"
                variant="outlined"
                placeholder="%Y-%m-%d"
                value={element.format || ''}
                onChange={(e) => onUpdate(index, { format: e.target.value })}
                error={!!element.format && !validateDateFormat(element.format).isValid}
                helperText={
                  element.format && !validateDateFormat(element.format).isValid
                    ? validateDateFormat(element.format).error
                    : undefined
                }
                sx={{
                  width: 120,
                  fontSize: 13,
                  backgroundColor: 'background.paper',
                }}
                InputProps={{
                  sx: { height: 32, minHeight: 32, fontSize: 13 },
                }}
                FormHelperTextProps={{
                  sx: { fontSize: 11, margin: '2px 0 0 0', whiteSpace: 'nowrap' },
                }}
              />
              <Tooltip
                title={
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}
                    >
                      Date Format Patterns:
                    </Typography>
                    {DATE_FORMAT_HELP.map((line) => (
                      <Typography key={line} variant="caption" sx={{ display: 'block' }}>
                        {line}
                      </Typography>
                    ))}
                  </Box>
                }
                arrow
                placement="top"
              >
                <InfoIcon sx={{ fontSize: 16, color: 'action.disabled', cursor: 'help', mt: 1 }} />
              </Tooltip>
            </>
          )}
        </>
      )}
      {element.type === 'DOCUMENT_DATA' && (
        <Select
          size="small"
          variant="outlined"
          value={element.value || ''}
          onChange={(e) => onUpdate(index, { value: e.target.value })}
          sx={{
            flexGrow: 1,
            minWidth: 150,
            fontSize: 13,
            height: 32,
            minHeight: 32,
            backgroundColor: 'background.paper',
          }}
        >
          <MenuItem value="">Select field...</MenuItem>
          {documentDataOptions.map((f) => (
            <MenuItem key={f.value} value={f.value}>
              {f.label}
            </MenuItem>
          ))}
        </Select>
      )}
      {element.type === 'SPLIT_VALUE' && (
        <Box
          sx={{
            flexGrow: 1,
            minWidth: 150,
            display: 'flex',
            alignItems: 'center',
            px: 1.25,
            height: 32,
            borderRadius: 1,
            border: '1px dashed',
            borderColor: splitBy ? 'divider' : 'warning.main',
            backgroundColor: 'grey.50',
            fontFamily: 'monospace',
            fontSize: 12,
            color: splitBy ? 'text.primary' : 'warning.dark',
          }}
        >
          {splitBy ? `{${splitBy}}` : 'Set Split By to use this element'}
        </Box>
      )}
      <IconButton size="small" onClick={() => onRemove(index)} sx={{ mt: 0.25 }}>
        <DeleteIcon />
      </IconButton>
    </Box>
  );
};

const DragClone: React.FC<{
  element: NamingElement;
  provided: DraggableProvided;
}> = ({ element, provided }) => {
  return (
    <Box
      ref={provided.innerRef}
      {...provided.draggableProps}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        backgroundColor: 'background.paper',
        boxShadow: 4,
        zIndex: 1300,
      }}
    >
      <span
        {...provided.dragHandleProps}
        style={{
          cursor: 'grab',
          display: 'flex',
          alignItems: 'center',
          marginRight: 8,
        }}
      >
        <DragIndicatorIcon color="disabled" />
      </span>
      <Select
        size="small"
        variant="outlined"
        value={element.type}
        sx={{
          width: 140,
          fontSize: 13,
          height: 32,
          minHeight: 32,
          backgroundColor: 'background.paper',
        }}
        disabled
      >
        <MenuItem value={element.type}>
          {element.type === 'STRING_LITERAL'
            ? 'String Literal'
            : element.type === 'METADATA'
              ? 'Metadata'
              : element.type === 'SPLIT_VALUE'
                ? 'Split Value'
                : 'Document Data'}
        </MenuItem>
      </Select>
      <TextField
        size="small"
        variant="outlined"
        value={element.value || ''}
        sx={{
          flexGrow: 1,
          minWidth: 150,
          fontSize: 13,
          backgroundColor: 'background.paper',
          height: 32,
        }}
        InputProps={{ sx: { height: 32, fontSize: 13 } }}
        disabled
      />
      <IconButton size="small" disabled>
        <DeleteIcon />
      </IconButton>
    </Box>
  );
};

const TemplateNamingSection: React.FC<TemplateNamingSectionProps> = ({
  namingElements,
  namingSeparator,
  namingMaxLength,
  splitBy,
  error,
  onAdd,
  onUpdate,
  onRemove,
  onMove,
  onSeparatorChange,
  onMaxLengthChange,
  getNamingPreview,
  documentDataOptions,
}) => {
  return (
    <>
      <Box sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <FormLabelStyled sx={{ mb: 0 }}>FILE NAMING</FormLabelStyled>
          <AddNewButton onClick={onAdd} />
        </Box>
        {error && (
          <Typography variant="body2" color="error" sx={{ mb: 1 }}>
            {error}
          </Typography>
        )}
        <Box
          sx={{
            mb: 2,
            p: 1,
            border: '1px solid',
            borderColor: error ? 'error.main' : 'divider',
            borderRadius: 2,
            backgroundColor: 'grey.50',
          }}
        >
          {namingElements.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>
              <Typography variant="body2">
                No custom naming - uses template_name_date.ext
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary', px: 1 }}>
                Build filename from elements
              </Typography>
              <DragDropContext
                onDragEnd={(result) => {
                  if (!result.destination) return;
                  onMove(result.source.index, result.destination.index);
                }}
              >
                <Droppable
                  droppableId="naming-elements-droppable"
                  renderClone={(provided, snapshot, rubric) => {
                    const element = namingElements[rubric.source.index];
                    return <DragClone element={element} provided={provided} />;
                  }}
                >
                  {(provided) => (
                    <Box ref={provided.innerRef} {...provided.droppableProps}>
                      {namingElements.map((element, index) => (
                        <Draggable
                          key={`naming-${index}`}
                          draggableId={`naming-${index}`}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <NamingElementRow
                              element={element}
                              index={index}
                              splitBy={splitBy}
                              documentDataOptions={documentDataOptions}
                              onUpdate={onUpdate}
                              onRemove={onRemove}
                              provided={provided}
                              snapshot={snapshot}
                            />
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </DragDropContext>
            </>
          )}
        </Box>
      </Box>

      {namingElements.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', mt: 2 }}>
          <Box sx={{ width: 120 }}>
            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
              Separator
            </Typography>
            <Select
              size="small"
              fullWidth
              variant="outlined"
              value={namingSeparator}
              onChange={(e) => onSeparatorChange(e.target.value as NamingSeparator)}
            >
              <MenuItem value="_">_ (underscore)</MenuItem>
              <MenuItem value="-">- (dash)</MenuItem>
              <MenuItem value=".">. (dot)</MenuItem>
            </Select>
          </Box>
          <Box sx={{ width: 100 }}>
            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
              Max Length
            </Typography>
            <TextField
              type="number"
              size="small"
              fullWidth
              variant="outlined"
              value={namingMaxLength}
              onChange={(e) => {
                const rawValue = e.target.value;
                if (rawValue === '') {
                  // Use minimum allowed value when field is cleared
                  onMaxLengthChange(10);
                } else {
                  const parsedValue = parseInt(rawValue, 10);
                  // Clamp to valid range (10-255)
                  const clampedValue = Math.max(10, Math.min(255, parsedValue));
                  onMaxLengthChange(clampedValue);
                }
              }}
              inputProps={{ min: 10, max: 255 }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
              Preview
            </Typography>
            <Box
              sx={{
                p: 1,
                bgcolor: 'action.hover',
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: 13,
                color: 'primary.main',
              }}
            >
              {getNamingPreview()}
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default TemplateNamingSection;
